import { Check, ExternalLink, X } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isPriceUnavailable } from '@/services/utils';
import type { OccupationalHealthServiceQuote } from '@/types/app';

interface ProviderProps {
	provider: OccupationalHealthServiceQuote;
	isSelected: boolean;
	isDisabled: boolean;
	isTransitioning: boolean;
	onSelect: (quoteId: string) => void;
	/** Read-only presentation: keeps the selected styling but removes the choose/deselect controls and card interaction. */
	readOnly?: boolean;
}

function Provider({
	provider,
	isSelected,
	isDisabled,
	isTransitioning,
	onSelect,
	readOnly = false
}: ProviderProps): React.JSX.Element {
	const { t } = useTranslation();
	const rateEur = Number.parseFloat(provider.rate);
	const isUnavailable = isPriceUnavailable(rateEur);
	const isBusy = isDisabled || isTransitioning || isUnavailable;
	const isEffectivelySelected = isSelected && !isUnavailable;
	const selectLabel = isUnavailable
		? t('quotes.item.notAvailable')
		: t('application.steps.step2.selectProvider', { name: provider.package_name });
	const deselectLabel = t('application.steps.step2.deselectProvider', {
		name: provider.package_name
	});

	function handleSelect(): void {
		if (isBusy) return;
		onSelect(provider.quote_id);
	}

	function handleKeyDown(e: React.KeyboardEvent): void {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleSelect();
		}
	}

	// Read-only cards are purely presentational: no click target, no button role.
	const interactiveProps = readOnly
		? {}
		: {
				onClick: handleSelect,
				onKeyDown: handleKeyDown,
				tabIndex: isBusy ? -1 : 0,
				role: 'button' as const,
				'aria-label': isEffectivelySelected ? deselectLabel : selectLabel
			};

	return (
		<div className="group relative h-full w-full">
			{isEffectivelySelected && (
				<div className="absolute -top-2 -right-2 z-10">
					{/* Without the button role and its aria-label, the checkmark is the only
					    selection cue left, so give it a text alternative. */}
					{readOnly && <span className="sr-only">{t('quotes.item.selected')}</span>}
					{/* Green checkmark: visible at rest on hover-capable devices, hidden on hover */}
					<div
						className={cn(
							'w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-md',
							!readOnly && 'group-hover:hidden [@media(hover:none)]:hidden'
						)}
					>
						<Check className="w-3 h-3 text-white" />
					</div>
					{/* Deselect button: shown on hover (pointer) or always on touch devices */}
					{!readOnly && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								handleSelect();
							}}
							disabled={isBusy}
							className="w-5 h-5 rounded-full bg-muted-foreground/60 hover:bg-muted-foreground items-center justify-center shadow-md hidden group-hover:flex [@media(hover:none)]:flex cursor-pointer"
							aria-label={deselectLabel}
						>
							<X className="w-3 h-3 text-white" />
						</button>
					)}
				</div>
			)}
			<div
				className={cn(
					'border rounded-lg overflow-hidden bg-white',
					isEffectivelySelected ? 'border-green-500' : 'border-border',
					isUnavailable && 'opacity-75'
				)}
			>
				<div
					className={cn(
						'flex flex-col items-center p-5 pt-7 gap-4',
						readOnly ? 'cursor-default' : isBusy ? 'pointer-events-none' : 'cursor-pointer'
					)}
					{...interactiveProps}
				>
					<div className="shrink-0 flex items-center justify-center px-10 h-9 w-full">
						<img
							src={`${import.meta.env.VITE_API_BASE_URL}/assets/providers/${provider.provider_slug}/logo.svg`}
							alt={provider.provider_name}
							className={cn(
								'max-w-full max-h-full w-auto h-9 object-contain',
								isUnavailable && 'grayscale opacity-50'
							)}
						/>
					</div>

					<div className="text-center px-2">
						<div className="text-sm font-bold text-foreground text-nowrap">
							{provider.package_name}
						</div>
					</div>

					<div className="shrink-0 text-center">
						<div
							className={cn(
								'font-bold text-3xl pe-2',
								isUnavailable ? 'text-muted-foreground' : 'text-foreground'
							)}
						>
							{isUnavailable ? '...' : `€ ${Math.round(rateEur).toLocaleString('nl-NL')}`}
						</div>
						<div className="text-muted-foreground text-xs whitespace-nowrap py-2">
							{isUnavailable
								? t('quotes.item.notAvailable')
								: t('application.steps.step2.perEmployeePerYear')}
						</div>
					</div>

					{/* aria-hidden + tabIndex={-1}: purely visual — keyboard users interact via the outer div[role=button] */}
					{!readOnly && (
						<div className="w-full shrink-0">
							<Button
								type="button"
								variant={isEffectivelySelected ? 'success' : 'default'}
								size="default"
								className="w-full font-bold px-4 py-2"
								onClick={(e) => {
									e.stopPropagation();
									handleSelect();
								}}
								disabled={isBusy}
								loading={isTransitioning}
								tabIndex={-1}
								aria-hidden
								data-testid="application-button-choose-arbodienstverlener"
							>
								{isEffectivelySelected
									? t('application.steps.step2.chosenButton')
									: t('application.steps.step2.chooseButton')}
							</Button>
						</div>
					)}
				</div>
				<div className="border-t border-border px-3 py-2 bg-secondary/30">
					<div className="flex justify-between">
						<a
							href={provider.url}
							target="_blank"
							rel="noopener noreferrer"
							className="text-foreground text-sm underline opacity-75 hover:opacity-100 inline-flex items-center gap-2"
							data-testid="quote-item-card-link"
						>
							<ExternalLink className="w-3 h-3" />
							{t('application.steps.step2.website')}
						</a>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Provider;
